/**
 * Parwaaz backend — e-mail templates.
 * Clean inline-styled HTML (no external assets — mail clients strip <style> and remote CSS)
 * with a plain-text alternative. Brand colours come from the tenant config.
 */
import type { TenantConfig } from '@parwaaz/shared';

export interface EmailContent {
  subject: string;
  html: string;
  text: string;
}

function shell(tenant: TenantConfig, heading: string, bodyHtml: string): string {
  const { primary, primaryStrong, ink } = tenant.theme;
  return `<!doctype html>
<html>
  <body style="margin:0;padding:0;background:#F5F7FA;font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:${ink};">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#F5F7FA;padding:24px 0;">
      <tr><td align="center">
        <table role="presentation" width="480" cellpadding="0" cellspacing="0" style="background:#FFFFFF;border-radius:12px;overflow:hidden;">
          <tr><td style="background:${primaryStrong};padding:20px 28px;">
            <span style="color:#FFFFFF;font-size:18px;font-weight:700;">${tenant.product.name}</span>
            <span style="color:#CFE0F0;font-size:13px;display:block;">${tenant.office.issuingAuthority}</span>
          </td></tr>
          <tr><td style="padding:28px;">
            <h1 style="margin:0 0 12px;font-size:20px;color:${ink};">${heading}</h1>
            <div style="font-size:15px;line-height:1.6;color:${ink};">${bodyHtml}</div>
          </td></tr>
          <tr><td style="background:${primary}10;padding:16px 28px;border-top:1px solid #E7ECF2;">
            <span style="font-size:12px;color:#5B6B7C;">${tenant.office.name} · ${tenant.office.address}</span>
          </td></tr>
        </table>
      </td></tr>
    </table>
  </body>
</html>`;
}

export function receivedEmail(tenant: TenantConfig, ref: string, statusUrl: string): EmailContent {
  const subject = `${tenant.product.name} — application received (${ref})`;
  const body = `<p>We have received your ${tenant.product.cardName} application, reference <b>${ref}</b>.</p>
<p>You can track its progress on your status page. Keep the link below — it is the only way to reopen your application without contacting the office:</p>
<p><a href="${statusUrl}" style="color:${tenant.theme.primary};">${statusUrl}</a></p>`;
  const text = `We have received your ${tenant.product.cardName} application, reference ${ref}.\nTrack it here: ${statusUrl}`;
  return { subject, html: shell(tenant, 'Application received', body), text };
}

export function correctionRequestedEmail(tenant: TenantConfig, ref: string, items: string[]): EmailContent {
  const subject = `${tenant.product.name} — action needed on ${ref}`;
  const list = items.map((i) => `<li>${i}</li>`).join('');
  const body = `<p>Your application <b>${ref}</b> needs a correction before it can proceed:</p>
<ul>${list}</ul>
<p>Open your saved status link to fix it.</p>`;
  const text = `Your application ${ref} needs a correction:\n${items.map((i) => `- ${i}`).join('\n')}\nOpen your saved status link to fix it.`;
  return { subject, html: shell(tenant, 'Correction needed', body), text };
}

export function approvedEmail(tenant: TenantConfig, ref: string): EmailContent {
  const subject = `${tenant.product.name} — ${ref} approved`;
  const body = `<p>Good news — your application <b>${ref}</b> has been approved. Your ${tenant.product.cardName} is being prepared.</p>
<p>Open your saved status link for updates.</p>`;
  const text = `Your application ${ref} has been approved. Open your saved status link for updates.`;
  return { subject, html: shell(tenant, 'Application approved', body), text };
}

export function rejectedEmail(tenant: TenantConfig, ref: string, reason: string): EmailContent {
  const subject = `${tenant.product.name} — ${ref} not approved`;
  const body = `<p>Your application <b>${ref}</b> was not approved.</p>
<p><b>Reason:</b> ${reason}</p>
<p>Contact ${tenant.office.grievanceOfficer} at ${tenant.office.phone} for questions.</p>`;
  const text = `Your application ${ref} was not approved.\nReason: ${reason}\nContact ${tenant.office.grievanceOfficer} at ${tenant.office.phone}.`;
  return { subject, html: shell(tenant, 'Application not approved', body), text };
}
